import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

// Shared Pro-plan helpers for features that only Pro stores get (Queue,
// kitchen/bar printers). The plan comes from the user profile.

const planName = (profile) => {
  const plan = profile?.plan || profile?.subscription?.plan || "";
  return typeof plan === "string" ? plan.toLowerCase() : "";
};

// The last plan we saw is also kept in localStorage so it survives reloads.
// It is keyed by role and the logged-in user (the `sub` of the login token), so
// a till that switches to another store never reads the previous store's plan.
// Without a readable token nothing is stored.
const planStorageKey = (role) => {
  try {
    const token = sessionStorage.getItem(`${role}_token`);
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const id = JSON.parse(atob(payload)).sub;
    return id ? `01pos_plan:${role}:${id}` : null;
  } catch {
    return null;
  }
};

const rememberPlan = (role, profile) => {
  const plan = planName(profile);
  const key = planStorageKey(role);
  if (!plan || !key) return;
  try {
    localStorage.setItem(key, plan);
  } catch {
    // Storage unavailable: only the in-memory cache remembers it.
  }
};

const storedPlan = (role) => {
  const key = planStorageKey(role);
  if (!key) return "";
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

export const planQuery = (api, role) => ({
  queryKey: ["plan", role],
  queryFn: async () => {
    const res = await api.get("/users/profile");
    const profile = res?.data?.data;
    rememberPlan(role, profile);
    return profile;
  },
  staleTime: 5 * 60 * 1000,
  retry: false,
});

// Strict: only a plan that is known to be "pro".
export const isProPlan = (profile) => planName(profile) === "pro";

// The plan to show in the UI: a fresh profile wins, otherwise the last one we
// saw, so a failed fetch doesn't flip a Pro store to locked.
// Loose lock: only a plan that is known and not "pro" locks. The Queue lets a
// store with unknown plan through because its API enforces the plan.
export const useIsPro = (api, role, enabled = true) => {
  const { data, isLoading } = useQuery({ ...planQuery(api, role), enabled });
  const plan = planName(data) || (enabled ? storedPlan(role) : "");
  return {
    isLoading,
    locked: plan !== "" && plan !== "pro",
    isPro: plan === "pro",
  };
};

// For code outside React (print time). Resolves to:
//   true  - the store is Pro
//   false - a profile says it is not Pro
//   null  - the plan can't be confirmed (never fetched, and the fetch failed)
// A failed fetch never downgrades a store: the last known plan (react-query
// cache, else the localStorage copy) is used and refreshed in the background.
export const fetchIsPro = async (queryClient, api, role) => {
  const known =
    planName(queryClient.getQueryData(["plan", role])) || storedPlan(role);

  const refresh = queryClient.fetchQuery(planQuery(api, role));
  if (known) {
    refresh.catch(() => {}); // background: the next print sees the new plan
    return known === "pro";
  }

  try {
    const plan = planName(await refresh);
    return plan ? plan === "pro" : null; // a profile with no plan info confirms nothing
  } catch {
    return null;
  }
};

// The "available exclusively on the Pro plan" popup. `children` is the
// feature description; `upgradePath` shows an Upgrade to Pro button.
export function ProFeatureModal({ open, onClose, upgradePath, title, children }) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[70] bg-black bg-opacity-40"
      style={{ backdropFilter: "blur(2px)" }}
    >
      <div className="bg-white text-black text-left p-6 rounded-lg max-w-lg shadow-lg w-[90vw] md:w-auto">
        <h2 className="text-xl mb-2 font-bold text-blue-700">{title}</h2>
        <div className="text-gray-700 mb-4">
          <p>
            <span className="font-semibold text-blue-700">{title}</span> is
            available exclusively on the Pro plan.
          </p>
          {children}
          <p className="mt-3">
            <span className="font-semibold text-blue-700">Tip:</span> Upgrade to
            Pro from your profile to unlock {title} for your store.
          </p>
        </div>
        <div className="flex flex-row gap-3 mt-2">
          <button
            className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            onClick={onClose}
          >
            Close
          </button>
          {upgradePath && (
            <button
              className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
              onClick={() => {
                onClose();
                navigate(upgradePath);
              }}
            >
              Upgrade to Pro
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
